import * as stylex from '@stylexjs/stylex';
import type { ReactElement } from 'react';
import { Collection } from 'react-aria-components';

import { ReportDateRangeTypeEnum, type components } from '@/libs/openapi';
import {
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectInput,
  SegmentedButton,
  SegmentedButtonGroup,
  useMediaQuery,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';

interface ReportSelectorProps {
  reports: components['schemas']['ReportResponseDto'][];
  selectedReport: components['schemas']['ReportResponseDto'] | undefined;
  onSelectionChange: (csvFileName: string) => void;
}

const SEGMENTED_MAX = 6;

export function ReportSelector ({ reports, selectedReport, onSelectionChange }: ReportSelectorProps): ReactElement | null {
  const isLargeScreen = useMediaQuery(breakpoints.large);

  if (reports.length <= 1) {
    return null;
  }

  const reversedReports = reports.toReversed();

  if (isLargeScreen && reports.length <= SEGMENTED_MAX) {
    return (
      <SegmentedButtonGroup
        disallowEmptySelection
        selectedKeys={selectedReport ? [ selectedReport.csvFileName ] : []}
        onSelectionChange={(keys) => {
          const key = [ ...keys ][0]?.toString();
          if (key !== undefined) {
            onSelectionChange(key);
          }
        }}>
        <Collection items={reversedReports}>
          {(item) => (
            <SegmentedButton id={item.csvFileName} style={segmentedButtonStyles.noWrap}>
              {formatReportPeriodLabel(item.dateRangeStart, item.dateRangeEnd, item.dateRangeType)}
            </SegmentedButton>
          )}
        </Collection>
      </SegmentedButtonGroup>
    );
  }

  const handleSelectChange = (key: string | number | null): void => {
    if (key !== null) {
      onSelectionChange(String(key));
    }
  };

  return (
    <Select
      aria-label="Report period"
      selectedKey={selectedReport?.csvFileName ?? null}
      style={selectStyles.base}
      onSelectionChange={handleSelectChange}>
      <SelectInput />
      <Popover>
        <ListBox items={reversedReports}>
          {(item) => (
            <ListBoxItem
              id={item.csvFileName}
              textValue={formatReportPeriodLabel(item.dateRangeStart, item.dateRangeEnd, item.dateRangeType)}>
              {formatReportPeriodLabel(item.dateRangeStart, item.dateRangeEnd, item.dateRangeType)}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </Select>
  );
}

function formatReportPeriodLabel (
  dateRangeStart: string,
  dateRangeEnd: string,
  dateRangeType: string,
): string {
  const start = new Date(dateRangeStart);
  const end = new Date(dateRangeEnd);
  const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });

  if (dateRangeType === ReportDateRangeTypeEnum.YEAR) {
    return start.getUTCFullYear().toString();
  }

  if (dateRangeType === ReportDateRangeTypeEnum.DAY) {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(start);
  }

  return `${formatter.format(start)} – ${formatter.format(end)}`;
}

const segmentedButtonStyles = stylex.create({
  noWrap: {
    whiteSpace: 'nowrap',
    width: 'fit-content',
  },
});

const selectStyles = stylex.create({
  base: {
    width: {
      default: 178,
      [breakpoints.small]: '100%',
    },
  },
});
